<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { stepFailure } from '../stepErrors';
import {
  STATUS_STYLE,
  REASON_STEPS,
  statusBody,
  statusMenuEntries,
  setStatusChoices,
} from '../ticketHelpers';
import StepModal from './StepModal.vue';
import SetStatusFlow from './SetStatusFlow.vue';
import AssignModal from './AssignModal.vue';
import BookingModal from './BookingModal.vue';

const MENU_WIDTH = 224;
const MENU_GAP = 4;

// The status of a ticket in the list. For an administrator it opens a menu of
// the changes the server allows right now (its `allowed_steps`, read when the
// menu opens); picking one runs the step. Steps that need input open the same
// dialogs as the ticket page: a reason for cancel, reopen and hand back, a
// person for assign, and the booking window after "needs a visit".
export default {
  name: 'CoLivingStatusDropdown',
  components: { StepModal, AssignModal, BookingModal, SetStatusFlow },
  props: {
    ticket: { type: Object, required: true },
    // Agents (no allowed steps) see a plain status.
    canChange: { type: Boolean, default: false },
  },
  emits: ['changed'],
  data() {
    return {
      statusStyle: STATUS_STYLE,
      isOpen: false,
      isLoading: false,
      loadError: '',
      entries: [],
      detail: null,
      menuStyle: {},
      isBusy: false,
      stepOpen: '',
      stepError: '',
      assignOpen: false,
      bookingOpen: false,
      choices: { allowed: [], blocked: [] },
      setStatusOpen: false,
    };
  },
  beforeDestroy() {
    this.unlisten();
  },
  methods: {
    ...mapActions('coLivingTickets', ['fetchAllowedSteps', 'changeStatus']),
    toggle() {
      if (this.isBusy) return;
      if (this.isOpen) this.close();
      else this.open();
    },
    // A fixed menu, so the list's scrolling wrapper cannot clip it.
    placeMenu() {
      const rect = this.$refs.pill.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
      this.menuStyle = {
        top: `${rect.bottom + MENU_GAP}px`,
        left: `${Math.max(8, left)}px`,
        width: `${MENU_WIDTH}px`,
      };
    },
    async open() {
      this.placeMenu();
      this.isOpen = true;
      this.listen();
      this.isLoading = true;
      this.loadError = '';
      this.entries = [];
      this.choices = { allowed: [], blocked: [] };
      try {
        const detail = await this.fetchAllowedSteps(this.ticket.id);
        if (!this.isOpen) return;
        this.detail = detail;
        this.entries = statusMenuEntries(detail.allowed_steps);
        this.choices = setStatusChoices(detail);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.TICKETS.STATUS_MENU.LOAD_FAILED'
        );
      } finally {
        this.isLoading = false;
      }
    },
    close() {
      this.isOpen = false;
      this.unlisten();
    },
    listen() {
      document.addEventListener('click', this.onOutside, true);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.close);
      window.addEventListener('scroll', this.onScroll, true);
    },
    unlisten() {
      document.removeEventListener('click', this.onOutside, true);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.close);
      window.removeEventListener('scroll', this.onScroll, true);
    },
    // The menu itself scrolls (it can be long); anything else moving under
    // it would leave it floating in the wrong place, so that closes it.
    onScroll(event) {
      const menu = this.$refs.menu;
      if (menu && event.target instanceof Node && menu.contains(event.target)) {
        return;
      }
      this.close();
    },
    onOutside(event) {
      if (!this.$el.contains(event.target)) this.close();
    },
    onKeydown(event) {
      if (event.code !== 'Escape') return;
      this.close();
      if (this.$refs.pill) this.$refs.pill.focus();
    },
    openSetStatus() {
      this.close();
      this.setStatusOpen = true;
    },
    choose(entry) {
      this.close();
      this.stepError = '';
      if (entry.step === 'assign') {
        this.assignOpen = true;
      } else if (REASON_STEPS.includes(entry.step)) {
        this.stepOpen = entry.step;
      } else {
        this.run(entry.step, {});
      }
    },
    async run(step, input) {
      this.isBusy = true;
      try {
        const result = await this.changeStatus({
          id: this.ticket.id,
          body: statusBody(step, input),
        });
        useAlert(this.$t('CO_LIVING.TICKETS.STEP_DONE'));
        this.stepOpen = '';
        this.$emit('changed');
        // The person calls the tenant next: go straight to the booking window.
        if (step === 'needs_visit') {
          this.detail = result.ticket;
          this.bookingOpen = true;
        }
      } catch (err) {
        const { message } = stepFailure(this, err);
        if (this.stepOpen) this.stepError = message;
        else useAlert(message);
        // The row may be stale (someone else moved the ticket): reload it.
        this.$emit('changed');
      } finally {
        this.isBusy = false;
      }
    },
    onAssignDone(result) {
      if (result !== null) {
        useAlert(this.$t('CO_LIVING.TICKETS.ASSIGN.DONE'));
      }
      this.$emit('changed');
    },
  },
};
</script>

<template>
  <span class="relative inline-block" @click.stop>
    <button
      v-if="canChange"
      ref="pill"
      type="button"
      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded hover:opacity-80 hover:ring-1 hover:ring-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
      :class="statusStyle[ticket.status]"
      :disabled="isBusy"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="$t('CO_LIVING.TICKETS.STATUS_MENU.TITLE')"
      :data-testid="`co-living-status-${ticket.id}`"
      @click="toggle"
    >
      {{ $t(`CO_LIVING.TICKETS.STATUS.${ticket.status}`) }}
      <fluent-icon icon="chevron-down" size="12" />
    </button>
    <span
      v-else
      class="px-2 py-0.5 text-xs font-medium rounded"
      :class="statusStyle[ticket.status]"
      :data-testid="`co-living-status-${ticket.id}`"
    >
      {{ $t(`CO_LIVING.TICKETS.STATUS.${ticket.status}`) }}
    </span>

    <div
      v-if="isOpen"
      ref="menu"
      class="fixed z-50 py-1 overflow-y-auto text-sm bg-white border rounded-lg shadow-lg max-h-96 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
      role="menu"
      :style="menuStyle"
      :data-testid="`co-living-status-menu-${ticket.id}`"
    >
      <p
        class="px-3 py-1 m-0 text-xs font-medium uppercase text-slate-500 dark:text-slate-400"
      >
        {{ $t('CO_LIVING.TICKETS.STATUS_MENU.TITLE') }}
      </p>
      <p
        v-if="isLoading"
        class="px-3 py-2 m-0 text-slate-500"
        data-testid="co-living-status-loading"
      >
        {{ $t('CO_LIVING.COMMON.LOADING') }}
      </p>
      <p
        v-else-if="loadError"
        class="px-3 py-2 m-0 text-red-500"
        data-testid="co-living-status-error"
      >
        {{ loadError }}
      </p>
      <p
        v-else-if="!entries.length && !choices.allowed.length"
        class="px-3 py-2 m-0 text-slate-500"
        data-testid="co-living-status-none"
      >
        {{ $t('CO_LIVING.TICKETS.STATUS_MENU.NONE') }}
      </p>
      <button
        v-for="entry in entries"
        :key="entry.step"
        type="button"
        role="menuitem"
        class="block w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:bg-slate-100 dark:focus:bg-slate-700 transition-colors"
        :class="
          entry.danger
            ? 'text-red-600 dark:text-red-400'
            : 'text-slate-900 dark:text-slate-100'
        "
        :data-testid="`co-living-status-option-${entry.step}`"
        @click="choose(entry)"
      >
        {{ $t(`CO_LIVING.TICKETS.${entry.label}`) }}
      </button>

      <button
        v-if="choices.allowed.length || choices.blocked.length"
        type="button"
        role="menuitem"
        class="block w-full px-3 py-2 text-left border-t text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:bg-slate-100 dark:focus:bg-slate-700 transition-colors"
        data-testid="co-living-set-status-open"
        @click="openSetStatus"
      >
        {{ $t('CO_LIVING.TICKETS.SET_STATUS.TITLE') }}
      </button>
    </div>

    <SetStatusFlow
      v-if="setStatusOpen"
      :ticket-id="ticket.id"
      :choices="choices"
      @close="setStatusOpen = false"
      @changed="$emit('changed')"
    />
    <StepModal
      v-if="stepOpen"
      :step="stepOpen"
      :error="stepError"
      :is-loading="isBusy"
      @close="stepOpen = ''"
      @confirm="run(stepOpen, $event)"
    />
    <AssignModal
      v-if="assignOpen && detail"
      :ticket="detail"
      @close="assignOpen = false"
      @done="onAssignDone"
    />
    <BookingModal
      v-if="bookingOpen && detail"
      :ticket="detail"
      @close="bookingOpen = false"
      @done="$emit('changed')"
    />
  </span>
</template>
